// Public /platform page (pt at /platform, en at /en/platform, es at
// /es/platform). Composed from the shared marketing components + the
// `platform` dictionary namespace; per-locale <head> with hreflang.

import { useNavigate } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { Button } from "@/components/ui/Button";
import { Card, Eyebrow, Section, SectionHeading } from "@/components/marketing/Section";
import { SiteFooter } from "@/components/marketing/SiteFooter";
import { SiteNav } from "@/components/marketing/SiteNav";
import {
  LOCALES,
  LOCALE_HREFLANG,
  localeUrl,
} from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";

const PAGE = "platform";
const CAPABILITY_ICONS = ["🔭", "🩸", "⚔", "🧩", "🧠", "📑", "🏢"];

export function PlatformPage() {
  const navigate = useNavigate();
  const locale = useLocale();
  const t = useDict().platform;
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));

  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <html lang={LOCALE_HREFLANG[locale]} />
        <title>{t.meta.title}</title>
        <meta name="description" content={t.meta.description} />
        <link rel="canonical" href={localeUrl(locale, PAGE)} />
        <meta property="og:url" content={localeUrl(locale, PAGE)} />
        <meta property="og:title" content={t.meta.title} />
        <meta property="og:description" content={t.meta.description} />
        {LOCALES.map((loc) => (
          <link
            key={loc}
            rel="alternate"
            hrefLang={LOCALE_HREFLANG[loc]}
            href={localeUrl(loc, PAGE)}
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href={localeUrl("pt", PAGE)} />
      </Head>

      <SiteNav page={PAGE} />

      {/* ── Hero ────────────────────────────────────────────── */}
      <Section width="900px">
        <div className="text-center">
          <Eyebrow>{t.hero.eyebrow}</Eyebrow>
          <h1
            className="font-display font-bold text-content mb-6 leading-[1.1]"
            style={{ fontSize: "clamp(30px, 5vw, 56px)" }}
          >
            {t.hero.title}
          </h1>
          <p className="font-sans text-[16px] sm:text-[18px] text-muted max-w-[680px] mx-auto leading-[1.6]">
            {t.hero.subline}
          </p>
        </div>
      </Section>

      {/* ── Capabilities ────────────────────────────────────── */}
      <Section alt width="1100px">
        <SectionHeading
          eyebrow={t.capabilities.eyebrow}
          title={t.capabilities.title}
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.capabilities.items.map((c, i) => (
            <Card key={c.title}>
              <div className="text-[26px] sm:text-[30px] mb-3">
                {CAPABILITY_ICONS[i]}
              </div>
              <h3 className="font-display text-[18px] font-bold text-content mb-2">
                {c.title}
              </h3>
              <p className="font-sans text-[13px] text-muted leading-[1.6]">
                {c.body}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* ── Workflow ────────────────────────────────────────── */}
      <Section width="900px">
        <SectionHeading eyebrow={t.workflow.eyebrow} title={t.workflow.title} />
        <ol className="grid sm:grid-cols-2 gap-4">
          {t.workflow.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center font-display text-[14px] font-bold"
                style={{
                  background: "rgb(var(--color-accent-bg))",
                  border: "1px solid rgb(var(--color-accent))",
                  color: "rgb(var(--color-accent))",
                }}
              >
                {i + 1}
              </span>
              <span className="font-sans text-[14px] text-muted leading-[1.6] pt-1">
                {step}
              </span>
            </li>
          ))}
        </ol>
      </Section>

      {/* ── Control ─────────────────────────────────────────── */}
      <Section alt width="1000px">
        <SectionHeading eyebrow={t.control.eyebrow} title={t.control.title} />
        <div className="grid md:grid-cols-3 gap-4">
          {t.control.items.map((c) => (
            <Card key={c.title}>
              <h3 className="font-display text-[16px] font-bold text-content mb-2">
                {c.title}
              </h3>
              <p className="font-sans text-[13px] text-muted leading-[1.6]">
                {c.body}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* ── Final CTA ───────────────────────────────────────── */}
      <Section width="800px">
        <div className="text-center">
          <Eyebrow>{t.cta.eyebrow}</Eyebrow>
          <h2 className="font-display text-[28px] sm:text-[38px] font-bold text-content mb-4 leading-[1.2]">
            {t.cta.title}
          </h2>
          <p className="font-sans text-[15px] sm:text-[16px] text-muted mb-8 leading-[1.6]">
            {t.cta.body}
          </p>
          <Button
            variant="primary"
            onClick={() => navigate(isAuthed ? "/app" : "/login")}
          >
            {t.cta.button}
          </Button>
        </div>
      </Section>

      <SiteFooter />
    </div>
  );
}
